import { useEffect } from "react";
import { motion } from "framer-motion";
import { Star, Gift, TrendingUp, Loader2 } from "lucide-react";
import { useLoyaltyStore, POINTS_PER_EURO, POINTS_REDEMPTION_RATE } from "@/stores/loyaltyStore";
import { useAuth } from "@/hooks/useAuth";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export const LoyaltyBadge = () => {
  const { user, isAuthenticated } = useAuth();
  const { 
    points, 
    totalEarned, 
    getAvailableDiscount, 
    transactions, 
    isLoading,
    isInitialized,
    initialize 
  } = useLoyaltyStore();
  
  const availableDiscount = getAvailableDiscount();
  const recentTransactions = transactions.slice(0, 5);

  useEffect(() => {
    if (isAuthenticated && user?.id && !isInitialized) {
      initialize(user.id);
    }
  }, [isAuthenticated, user?.id, isInitialized, initialize]);

  // Don't show badge if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center gap-1 px-1.5 py-0.5 sm:gap-1.5 sm:px-2 sm:py-1 lg:gap-2 lg:px-3 lg:py-1.5 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-full hover:border-amber-500/40 transition-colors"
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 lg:h-4 lg:w-4 animate-spin text-amber-500" />
          ) : (
            <Star className="h-3 w-3 sm:h-3.5 sm:w-3.5 lg:h-4 lg:w-4 text-amber-500 fill-amber-500" />
          )}
          <span className="text-[10px] sm:text-xs lg:text-sm font-medium">{points}<span className="hidden sm:inline"> pts</span></span>
        </motion.button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 z-[70]" align="end">
        <div className="bg-gradient-to-br from-amber-500 to-orange-500 p-4 text-white rounded-t-lg">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">Loyalty Program</h3>
            <Star className="h-5 w-5 fill-white" />
          </div>
          <div className="text-3xl font-bold mb-1">{points} points</div>
          <p className="text-sm text-white/80">
            Value: €{availableDiscount} discount
          </p>
        </div>

        <div className="p-4 space-y-4">
          {/* How it works */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              How It Works
            </h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Earn <strong>{POINTS_PER_EURO} points</strong> per €1 spent</li>
              <li>• <strong>{POINTS_REDEMPTION_RATE} points</strong> = €1 discount</li>
              <li>• Use your points on any order</li>
            </ul>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-secondary/50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-primary">{totalEarned}</div>
              <div className="text-xs text-muted-foreground">Points Earned</div>
            </div>
            <div className="bg-secondary/50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-green-600">€{availableDiscount}</div>
              <div className="text-xs text-muted-foreground">Available</div>
            </div>
          </div>

          {/* Recent transactions */}
          {recentTransactions.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Gift className="h-4 w-4 text-primary" />
                Recent Activity
              </h4>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {recentTransactions.map((tx) => (
                  <div
                    key={tx.id}
                    className="flex items-center justify-between text-xs py-1.5 border-b border-border/50 last:border-0"
                  >
                    <span className="text-muted-foreground truncate max-w-[180px]">
                      {tx.description}
                    </span>
                    <span
                      className={`font-medium ${
                        tx.type === 'earned' ? 'text-green-600' : 'text-red-500'
                      }`}
                    >
                      {tx.type === 'earned' ? '+' : '-'}{tx.points} pts
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recentTransactions.length === 0 && (
            <div className="text-center py-4 text-muted-foreground text-sm">
              <Gift className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No transactions yet</p>
              <p className="text-xs">Make a purchase to start earning!</p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
